import { METERS_PER_DEGREE_LAT } from "./geo";
import { metresFromSegment } from "./resolveRoutePath";


// Widening the catch area makes neighbouring routes overlap, and Google
// delivers an overlapped event to whichever line is on top - effectively
// draw order. So the catch lines do not act on their own events: they
// report the cursor to this layer, which measures the distance to every
// eligible route and dispatches to the nearest one. Routes that are
// genuinely coincident (same street, two trips) cannot be separated this
// way and resolve to a stable arbitrary pick for now; a list to choose a
// trip out of the overlap is the follow-up.

type LatLng = { lat: number; lng: number };

export type RouteHitHandlers = {
  onEnter?: () => void;
  onMove?: (point: LatLng, domEvent: MouseEvent) => void;
  onLeave?: () => void;
  onClick?: (point: LatLng, domEvent: MouseEvent) => void;
};

export type RouteLine = {
  setPath: (path: LatLng[]) => void;
  setMap: (nextMap: any) => void;
  setOptions: (options: any) => void;
  destroy: () => void;
};


const HIT_WEIGHT_FAR = 10;
const HIT_WEIGHT_NEAR = 16;
const ZOOM_NEAR = 15;

const EQUATOR_METRES_PER_PIXEL = 156543.03392;

// two routes closer together than this are a tie rather than a winner, and
// resolve on trip key so the same cursor position always picks the same one
const TIE_METRES = 0.5;

const hitWeightForZoom = (zoom: number) =>
  zoom >= ZOOM_NEAR ? HIT_WEIGHT_NEAR : HIT_WEIGHT_FAR;

const metresPerPixel = (lat: number, zoom: number) =>
  (EQUATOR_METRES_PER_PIXEL * Math.cos((lat * Math.PI) / 180)) /
  Math.pow(2, zoom);

type Bounds = {
  minLat: number;
  maxLat: number;
  minLng: number;
  maxLng: number;
};

export const createRouteHitLayer = ({ map }: { map: any }) => {
  const google = (window as any).google;

  type Registration = {
    tripKey: string;
    handlers: RouteHitHandlers;
    getPath: () => LatLng[];
    isEligible: () => boolean;
    hitPolyline: any;
  };

  const registrations: Registration[] = [];

  // keyed on the path array itself, so a snap-to-road upgrade - which hands
  // over a new array - gets measured afresh while a path that has not moved
  // is measured once
  const boundsCache = new WeakMap<object, Bounds>();

  let claimedTripKey: string | null = null;
  let pendingRelease = 0;
  let cursorApplied = false;
  const originalCursor = map.get?.("draggableCursor") ?? null;

  // several overlapping catch lines fire for one cursor position, so the
  // winner is worked out once and reused for the rest of them
  let lastQuery: {
    lat: number;
    lng: number;
    zoom: number;
    winner: string | null;
  } | null = null;

  const boundsOf = (path: LatLng[]): Bounds => {
    const cached = boundsCache.get(path);
    if (cached) return cached;
    let minLat = Infinity;
    let maxLat = -Infinity;
    let minLng = Infinity;
    let maxLng = -Infinity;
    for (let index = 0; index < path.length; index += 1) {
      const point = path[index];
      if (point.lat < minLat) minLat = point.lat;
      if (point.lat > maxLat) maxLat = point.lat;
      if (point.lng < minLng) minLng = point.lng;
      if (point.lng > maxLng) maxLng = point.lng;
    }
    const bounds = { minLat, maxLat, minLng, maxLng };
    boundsCache.set(path, bounds);
    return bounds;
  };

  // the cursor is within reach of at most a handful of segments, so each one
  // is rejected on its own box first. A route's own box spans its whole day
  // and so rarely excludes a cursor anywhere in town, which left the walk
  // below measuring every segment of every route in view on every move.
  const metresToPath = (
    path: LatLng[],
    point: LatLng,
    latPad: number,
    lngPad: number
  ) => {
    const minLat = point.lat - latPad;
    const maxLat = point.lat + latPad;
    const minLng = point.lng - lngPad;
    const maxLng = point.lng + lngPad;
    let best = Infinity;
    for (let index = 1; index < path.length; index += 1) {
      const from = path[index - 1];
      const to = path[index];
      if (from.lat < minLat && to.lat < minLat) continue;
      if (from.lat > maxLat && to.lat > maxLat) continue;
      if (from.lng < minLng && to.lng < minLng) continue;
      if (from.lng > maxLng && to.lng > maxLng) continue;
      const distance = metresFromSegment(point, from, to);
      if (distance < best) {
        best = distance;
        if (best === 0) return 0;
      }
    }
    return best;
  };

  const winnerAt = (point: LatLng): string | null => {
    const zoom = map.getZoom?.() ?? 0;
    if (
      lastQuery &&
      lastQuery.zoom === zoom &&
      lastQuery.lat === point.lat &&
      lastQuery.lng === point.lng
    ) {
      return lastQuery.winner;
    }

    const toleranceMetres =
      (hitWeightForZoom(zoom) / 2) * metresPerPixel(point.lat, zoom);
    // a box check first, so a cursor nowhere near a route never costs a walk
    // along its segments
    const latPad = toleranceMetres / METERS_PER_DEGREE_LAT;
    const lngPad =
      latPad / Math.max(Math.cos((point.lat * Math.PI) / 180), 0.01);

    const scored: { tripKey: string; distance: number }[] = [];
    registrations.forEach((registration) => {
      if (!registration.isEligible()) return;
      const path = registration.getPath();
      if (path.length < 2) return;
      const bounds = boundsOf(path);
      if (
        point.lat < bounds.minLat - latPad ||
        point.lat > bounds.maxLat + latPad ||
        point.lng < bounds.minLng - lngPad ||
        point.lng > bounds.maxLng + lngPad
      ) {
        return;
      }
      const distance = metresToPath(path, point, latPad, lngPad);
      if (distance > toleranceMetres) return;
      scored.push({ tripKey: registration.tripKey, distance });
    });

    scored.sort(
      (a, b) =>
        (Math.abs(a.distance - b.distance) <= TIE_METRES
          ? 0
          : a.distance - b.distance) || a.tripKey.localeCompare(b.tripKey)
    );

    const winner = scored.length > 0 ? scored[0].tripKey : null;
    lastQuery = { lat: point.lat, lng: point.lng, zoom, winner };
    return winner;
  };

  const registrationFor = (tripKey: string | null) =>
    tripKey === null
      ? null
      : registrations.find(
          (registration) => registration.tripKey === tripKey
        ) ?? null;

  const applyCursor = (on: boolean) => {
    if (on === cursorApplied) return;
    cursorApplied = on;
    // half of the finickiness was not knowing whether a route was armed yet
    map.setOptions({ draggableCursor: on ? "pointer" : originalCursor });
  };

  const setClaim = (tripKey: string | null) => {
    if (tripKey === claimedTripKey) return;
    registrationFor(claimedTripKey)?.handlers.onLeave?.();
    claimedTripKey = tripKey;
    registrationFor(claimedTripKey)?.handlers.onEnter?.();
    applyCursor(tripKey !== null);
  };

  const cancelPendingRelease = () => {
    if (!pendingRelease) return;
    cancelAnimationFrame(pendingRelease);
    pendingRelease = 0;
  };

  // crossing out of one catch band into an overlapping one fires the first
  // line's mouseout before the second line's mousemove, so the claim is
  // dropped a frame late - otherwise the highlight flickers off and on at
  // every crossing
  const pointerOut = () => {
    cancelPendingRelease();
    pendingRelease = requestAnimationFrame(() => {
      pendingRelease = 0;
      setClaim(null);
    });
  };

  const pointerMove = (point: LatLng, domEvent: MouseEvent) => {
    cancelPendingRelease();
    const winner = winnerAt(point);
    setClaim(winner);
    if (winner === null) return;
    registrationFor(winner)?.handlers.onMove?.(point, domEvent);
  };

  const pointerClick = (point: LatLng, domEvent: MouseEvent) => {
    const winner = winnerAt(point);
    if (winner === null) return;
    registrationFor(winner)?.handlers.onClick?.(point, domEvent);
  };

  const applyHitWeights = () => {
    const strokeWeight = hitWeightForZoom(map.getZoom?.() ?? 0);
    registrations.forEach((registration) =>
      registration.hitPolyline.setOptions({ strokeWeight })
    );
  };

  const zoomListener = map.addListener("zoom_changed", () => {
    lastQuery = null;
    applyHitWeights();
  });

  const createRouteLine = ({
    tripKey,
    visibleOptions,
    handlers,
  }: {
    tripKey: string;
    visibleOptions: any;
    handlers: RouteHitHandlers;
  }): RouteLine => {
    const visible = new google.maps.Polyline({
      ...visibleOptions,
      // the drawn line never competes for the cursor
      clickable: false,
    });

    let currentPath: LatLng[] = visibleOptions.path ?? [];
    let onMap = Boolean(visibleOptions.map);
    let clickable = visibleOptions.clickable !== false;

    const hit = new google.maps.Polyline({
      path: currentPath,
      map: onMap ? map : null,
      strokeColor: "#000000",
      strokeOpacity: 0,
      strokeWeight: hitWeightForZoom(map.getZoom?.() ?? 0),
      clickable: true,
      zIndex: visibleOptions.zIndex ?? 1,
    });

    hit.addListener("mousemove", (event: any) => {
      if (!event.latLng) return;
      pointerMove(
        { lat: event.latLng.lat(), lng: event.latLng.lng() },
        event.domEvent as MouseEvent
      );
    });
    hit.addListener("mouseout", pointerOut);
    hit.addListener("click", (event: any) => {
      if (!event.latLng) return;
      pointerClick(
        { lat: event.latLng.lat(), lng: event.latLng.lng() },
        event.domEvent as MouseEvent
      );
    });

    const registration: Registration = {
      tripKey,
      handlers,
      getPath: () => currentPath,
      isEligible: () => onMap && clickable,
      hitPolyline: hit,
    };
    registrations.push(registration);

    const dropClaimIfMine = () => {
      if (claimedTripKey === tripKey) setClaim(null);
    };

    return {
      setPath: (path) => {
        currentPath = path;
        visible.setPath(path);
        // the catch area follows the drawn path rather than the full one, so
        // a route still replaying in is not hoverable ahead of itself
        hit.setPath(path);
        lastQuery = null;
      },
      setMap: (nextMap) => {
        onMap = Boolean(nextMap);
        visible.setMap(nextMap);
        hit.setMap(nextMap);
        if (!onMap) dropClaimIfMine();
        lastQuery = null;
      },
      setOptions: (options) => {
        visible.setOptions(options);
        // the catch line keeps its own width and stays invisible; only what
        // decides whether it takes part carries over
        if ("clickable" in options) {
          clickable = options.clickable !== false;
          hit.setOptions({ clickable: options.clickable });
          if (!clickable) dropClaimIfMine();
        }
        if ("zIndex" in options) hit.setOptions({ zIndex: options.zIndex });
        lastQuery = null;
      },
      destroy: () => {
        dropClaimIfMine();
        google.maps.event.clearInstanceListeners(hit);
        hit.setMap(null);
        visible.setMap(null);
        const index = registrations.indexOf(registration);
        if (index >= 0) registrations.splice(index, 1);
        lastQuery = null;
      },
    };
  };

  return {
    createRouteLine,
    destroy: () => {
      cancelPendingRelease();
      applyCursor(false);
      google.maps.event.removeListener(zoomListener);
      registrations
        .slice()
        .forEach((registration) =>
          google.maps.event.clearInstanceListeners(registration.hitPolyline)
        );
    },
  };
};
